import clsx from 'clsx'
import PropTypes from 'prop-types'
import Avatar from 'react-avatar'
import { connect } from 'react-redux'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import FlatLink from '../../../../UI/FlatLink'
import Location from '../../../../UI/location'
import NewsHighlights from '../../../../UI/NewsHighlights'
import CompanyProgress from '../../../../UI/CompanyProgress'
import CompanySteps from '../../common/CompanySteps'
import CompanyPicker from './components/CompanyPicker'
import HygieneTraining from '../../common/HygieneTraining'
import NotificationsPreview from '../../common/NotificationsPreview'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { useCompanyStepFocus } from '../../../../shared/companyStepLink'

import styles from './style.module.scss'
import { useAvatarUrl } from '../../../../shared/useAvatarUrls'

/**
 * Dashboard der Lehrkraft.
 *
 * Darunter standen bis 07.08.2026 drei gleich aussehende Listen: Neuigkeiten,
 * „Alle Unternehmen in dieser Schule" und „Alle Schüler:innen in dieser
 * Schule". Die beiden letzten wiederholten nur, was die Menüpunkte
 * „Unternehmen" und „Schüler:innen" vollständig, durchsuchbar und filterbar
 * zeigen.
 *
 * Seit 08.08.2026 steht hier dasselbe Raster wie bei den Schüler:innen: was zu
 * tun ist, die Mitteilungen, die Neuigkeiten. Die erste Spalte gehört einer
 * **Junior Company** — eine Lehrkraft betreut mehrere, deshalb steht über den
 * Spalten, welche gerade gemeint ist, und bei mehr als einer eine Auswahl
 * daneben. Ohne diese Zeile stünde dort ein Stand ohne Angabe, wessen Stand.
 *
 * Gelesen wird nur: Uploads, Fenster und die Verweise in die Menüpunkte der
 * Schüler:innen bleiben weg (`readOnly`). Zum Handeln gibt es die
 * Schüler:innen-Ansicht über die Liste der Schüler:innen.
 */

// Welche Company zuletzt gewählt war — sonst beginnt jeder Besuch wieder bei
// der ersten, und wer eine bestimmte betreut, wählt sie jedes Mal neu.
const SPEICHER_SCHLUESSEL = 'teacherDashboardCompanyId'

function Dashboard(props) {
	const { t } = useTranslation()
	const [dashboard, setDashboard] = useState({ news: [], supervisedCompanies: [] })
	const [companyId, setCompanyId] = useState(null)
	const [companyStand, setCompanyStand] = useState(null)
	const [ladeStand, setLadeStand] = useState(false)

	// Balken und Checkliste zeigen denselben Ablauf — welcher Schritt in beiden
	// hervorgehoben ist, steht hier (siehe shared/companyStepLink).
	const { focusId, focusStep } = useCompanyStepFocus()

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein
	// <img src> könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarSrc = useAvatarUrl(props.avatarHashName)

	const companies = dashboard.supervisedCompanies || []
	const company = companies.find(item => item.id === companyId)

	useEffect(() => {
		http(TApi.TEACHER_GET_DASHBOARD).then(({ data }) => {
			setDashboard(data)

			const betreute = data.supervisedCompanies || []
			if (!betreute.length) return

			const gemerkt = Number(window.localStorage.getItem(SPEICHER_SCHLUESSEL))
			const treffer = betreute.find(item => item.id === gemerkt)

			setCompanyId(treffer ? treffer.id : betreute[0].id)
		})
	}, [])

	useEffect(() => {
		if (!companyId) return

		window.localStorage.setItem(SPEICHER_SCHLUESSEL, String(companyId))

		setLadeStand(true)
		setCompanyStand(null)
		http.get(TApi.TEACHER_GET_COMPANY_DASHBOARD(companyId))
			.then(({ data }) => setCompanyStand(data))
			.finally(() => setLadeStand(false))
	}, [companyId])

	return (
		<div className={styles.root}>
			<div className={clsx(styles.userWrapperContent, 'container')}>
				<div className={styles.userWrapper}>
					<div className={styles.user}>
						<Avatar
							src={avatarSrc}
							className={styles.userAvatar}
							size={76}
							round
							name={`${props.firstName} ${props.lastName}`}
						/>
						<div className={styles.userNameWrapper}>
							<p className={styles.helloMessage}>{t('Hello, welcome back')}</p>
							<span className={styles.userName}>{props.firstName}</span>
						</div>
					</div>
					<div className={styles.schoolWrapper}>
						<Location
							label={
								props.gender === 'MALE'
									? t('Supervision MaleTeacher in')
									: t('Supervision FemaleTeacher in')
							}
							title={props.school?.name}
							allInfo={[
								props.school?.location,
								[props.school?.streetName, props.school?.number].filter(Boolean).join(' ')
							]
								.map(teil => (teil || '').trim())
								.filter(Boolean)
								.join(', ')}
						/>
					</div>
					<FlatLink
						className={styles.editProfile}
						color="#303E65"
						icon="arrow"
						classNameIcon={styles.editProfileIcon}
						iconColor="#008B9C"
						iconSize={{ w: 10, h: 10 }}
						to={TRoutes.PROFILE}
					>
						{t('Edit My Profile')}
					</FlatLink>
				</div>
			</div>

			{/*
			 * Wessen Stand hier steht, muss ohne Nachdenken lesbar sein — und ebenso,
			 * dass sich das wechseln lässt. Beides sagt derselbe Knopf: der Name ist
			 * die Auswahl (siehe components/CompanyPicker). Bei genau einer betreuten
			 * Company gibt es nichts zu wählen — dann steht nur der Name da.
			 */}
			{!!companies.length && (
				<div className={clsx('container', styles.companyBar)}>
					<CompanyPicker companies={companies} companyId={companyId} onChange={setCompanyId} />
				</div>
			)}

			{/*
			 * Derselbe Fortschrittsbalken wie bei den Schüler:innen — für die
			 * oben gewählte Company. Er beantwortet die Frage, mit der eine
			 * Lehrkraft auf das Dashboard kommt („wie weit sind die?"), ohne das
			 * Firmenprofil zu öffnen.
			 */}
			{!!companies.length && (
				<div className={clsx('container', styles.progress)}>
					<CompanyProgress
						companyType={company?.companyType}
						activationsStatus={companyStand?.activationsStatus}
						isActivated={!!company?.isActive}
						isNeedReview={!!company?.isNeedReview}
						isLoading={ladeStand}
						focusId={focusId}
						onFocusStep={focusStep}
					/>
				</div>
			)}

			{/*
			 * Lebensmittelhygiene und Allergeninformation (Anforderung #96).
			 * Steht nur da, wenn die gewählte Junior Company in einer sensiblen
			 * Branche unterwegs ist — der Baustein fragt das selbst beim Server
			 * nach. Hier ist der Ort, an dem die Lehrkraft die Angabe der
			 * Schüler:innen sieht und ihr widersprechen kann; im
			 * Unternehmensprofil ist sie nur zu Gast (Schüler:innen-Ansicht).
			 */}
			{!!companyId && (
				<div className={clsx('container', styles.progress)}>
					<HygieneTraining
						companyId={companyId}
						savedBranch={company?.branch}
						onChanged={() => {
							setLadeStand(true)
							http.get(TApi.TEACHER_GET_COMPANY_DASHBOARD(companyId))
								.then(({ data }) => setCompanyStand(data))
								.finally(() => setLadeStand(false))
						}}
					/>
				</div>
			)}

			<div className={clsx('container', styles.columns)}>
				{companies.length ? (
					<CompanySteps
						readOnly
						companyType={company?.companyType}
						isActivated={!!company?.isActive}
						isNeedReview={!!company?.isNeedReview}
						activationsStatus={companyStand?.activationsStatus}
						businessPlan={companyStand?.businessPlan}
						finalReport={companyStand?.finalReport}
						isLoading={ladeStand}
						focusId={focusId}
						onFocusStep={focusStep}
					/>
				) : (
					<p className={styles.noCompany}>{t('You are not supervising any Junior Company yet.')}</p>
				)}

				<NotificationsPreview embedded />

				<NewsHighlights single items={dashboard.news} toFullList={TRoutes.NEWS} />
			</div>
		</div>
	)
}

Dashboard.propTypes = {
	firstName: PropTypes.string.isRequired,
	lastName: PropTypes.string.isRequired,
	avatarHashName: PropTypes.string,
	school: PropTypes.object,
	gender: PropTypes.string
}

const mapStateToProps = state => ({
	firstName: state.user.profile.firstName,
	lastName: state.user.profile.lastName,
	school: state.user.profile.School,
	avatarHashName: state.user.profile.avatarHashName,
	gender: state.user.profile.gender
})

export default connect(mapStateToProps)(Dashboard)
